import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { PageTitle } from "../../components/Layout";
import { Badge, fmtDate, flattenEtapes } from "../../lib/helpers";
import * as Icons from "lucide-react";
import AuthImg from "../../components/AuthImg";
import AuthFileLink from "../../components/AuthFileLink";

const isImg = (name = "") => /\.(png|jpe?g|webp|svg)$/i.test(name);
const dueDate = (p) => { const ds = flattenEtapes(p).map((et) => et.planned_date).filter(Boolean).sort(); return ds.length ? ds[ds.length - 1] : null; };

export default function CollabClientDetail() {
  const { cid } = useParams();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/collab/clients/${cid}`).then((r) => setD(r.data)).catch(() => nav("/clients")); }, [cid, nav]);
  if (!d) return null;

  const ident = d.identity;
  const hasIdent = ident && ((ident.palette || []).length || (ident.typography || []).length || (ident.logos || []).length);

  return (
    <div className="max-w-4xl">
      <button onClick={() => nav("/clients")} className="text-sm text-marine/80 hover:text-marine flex items-center gap-1 mb-4"><Icons.ArrowLeft size={16} className="text-marine" /> Retour aux entreprises</button>
      <PageTitle overline="Fiche client · Lecture seule" title={d.client.name} />

      {d.client.email && (
        <div className="text-sm text-marine/80 mb-8 flex flex-wrap gap-x-4 gap-y-1">
          <span className="flex items-center gap-1"><Icons.Mail size={16} className="text-marine" /> {d.client.email}</span>
        </div>
      )}

      <h2 className="font-display text-xl text-marine mb-4">Projets</h2>
      <div className="grid sm:grid-cols-2 gap-4 mb-12">
        {d.projects.length === 0 && <p className="text-sm text-marine/80">Aucun projet accessible.</p>}
        {d.projects.map((p) => {
          const done = p.progress === 100;
          const due = dueDate(p);
          return (
            <button key={p.id} onClick={() => nav(`/clients/${cid}/projets/${p.id}`)} data-testid={`collab-project-${p.id}`}
              className={`text-left bg-sable rounded-sm border border-marine/10 shadow-sm p-5 card-lift hover:border-marine/40 ${done ? "opacity-55 grayscale" : ""}`}>
              <div className="flex items-start justify-between mb-2">
                <div className="text-xs uppercase tracking-wider text-bordeaux font-semibold">{p.type_label}</div>
                <span className={`shrink-0 whitespace-nowrap text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-sm font-medium ${done ? "bg-bleu-50 border border-bleu/30 text-bleu" : "bg-marine-50 text-marine"}`}>{done ? "Finalisé" : "En cours"}</span>
              </div>
              <div className="font-display text-lg text-marine mb-2">{p.name}</div>
              <div className="text-xs text-marine/80 flex items-center gap-1.5 mb-3"><Icons.CalendarClock size={16} className="text-bordeaux" /> {due ? `Rendu : ${fmtDate(due)}` : "Rendu : à planifier"}</div>
              <div className="flex items-center gap-2"><div className="flex-1 h-1.5 bg-sable border border-marine/30 rounded-full overflow-hidden"><div className="h-full bg-marine" style={{ width: `${p.progress}%` }} /></div><span className="text-xs text-marine font-medium">{p.progress}%</span></div>
            </button>
          );
        })}
      </div>

      {/* Plus de « Documents » de l'entreprise : un collaborateur ne voit aucune information contractuelle (02/10). */}
      {hasIdent && (
        <>
          <h2 className="font-display text-xl text-marine mb-4">Identité Visuelle</h2>
          <div className="bg-sable rounded-sm border border-marine/10 shadow-sm p-6 space-y-6">
            {(ident.logos || []).length > 0 && (
              <div>
                <div className="text-xs uppercase tracking-wider text-marine/80 mb-2">Logos</div>
                <div className="flex flex-wrap gap-3">
                  {ident.logos.map((l, i) => (
                    <AuthFileLink key={i} path={l.path} className="w-28 h-20 rounded-sm border border-marine/10 flex items-center justify-center p-2 beige-texture">
                      {l.path && isImg(l.name || l.path) ? <AuthImg path={l.path} alt={l.label} className="max-h-full max-w-full object-contain" /> : <Icons.Image size={16} className="text-marine/30" />}
                    </AuthFileLink>
                  ))}
                </div>
              </div>
            )}
            {(ident.palette || []).length > 0 && (
              <div>
                <div className="text-xs uppercase tracking-wider text-marine/80 mb-2">Couleurs</div>
                <div className="flex flex-wrap gap-3">
                  {ident.palette.map((c, i) => (
                    <div key={i} className="text-center">
                      <div className="w-16 h-16 rounded-sm border border-marine/10" style={{ background: c.hex }} />
                      <div className="text-xs text-marine mt-1">{c.name}</div>
                      <div className="text-[10px] text-marine/80 font-mono uppercase">{c.hex}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {(ident.typography || []).length > 0 && (
              <div>
                <div className="text-xs uppercase tracking-wider text-marine/80 mb-2">Typographies</div>
                <div className="space-y-2">
                  {ident.typography.map((t, i) => {
                    const serif = /playfair|serif|georgia|times/i.test(t.name);
                    return <div key={i} className="border border-marine/10 rounded-sm p-3"><span className="text-xs text-bordeaux uppercase">{t.usage}</span><div className={`text-2xl text-marine ${serif ? "font-display" : "font-sans"}`}>{t.name}</div></div>;
                  })}
                </div>
              </div>
            )}
            {(ident.files || []).length > 0 && (
              <div className="flex flex-wrap gap-2">
                {ident.files.map((f, i) => <AuthFileLink key={i} path={f.path} className="flex items-center gap-2 border border-marine/10 rounded-sm p-2 text-xs hover:border-marine/40"><Icons.File size={16} className="text-marine" /> {f.label || f.name} <span className="text-bordeaux">({f.kind})</span></AuthFileLink>)}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
